import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Legend, BarChart, Bar, ComposedChart, Area } from 'recharts';
import { Activity, Users, Download, TrendingUp, Clock, ChevronRight, BarChart2 } from 'lucide-react';
import SpatialNavigationAnalytics from './SpatialNavigationAnalytics';

interface GaitMetrics {
  timestamp: Date;
  walkingSpeed: number;
  stepLength: number;
  stepTime: number;
  strideVariability: number;
  stanceTime: number;
  turningSpeed: number;
  wanderingEvents: number;
}

interface Resident {
  id: string;
  name: string;
  age: number;
  diagnosis: string;
  diagnosisDate: Date;
  riskLevel: 'low' | 'medium' | 'high';
  gaitHistory: GaitMetrics[];
  cohortGroup: string;
}

interface AggregateMetrics {
  diagnosisGroup: string;
  averageProgression: number;
  patientCount: number;
  metrics: {
    avgWalkingSpeed: number;
    avgStrideVariability: number;
    avgTurningSpeed: number;
    avgWanderingEvents: number;
    riskScore: number;
  };
  timeToDeterioration: number;
  interventionHistory: InterventionData[];
}

interface InterventionData {
  month: number;
  riskScore: number;
  interventionCount: number;
  adherenceRate: number;
}

const mockResidents: Resident[] = Array.from({ length: 12 }, (_, i) => {
  const diagnosisDate = new Date();
  diagnosisDate.setMonth(diagnosisDate.getMonth() - Math.floor(Math.random() * 24));
  
  const gaitHistory = Array.from({ length: 90 }, (_, j) => {
    const date = new Date();
    date.setDate(date.getDate() - j);
    const baseSpeed = 1.2 - (j * 0.002) - (Math.random() * 0.1);
    
    return {
      timestamp: date,
      walkingSpeed: Math.max(0.5, baseSpeed),
      stepLength: 0.6 - (j * 0.001) + (Math.random() * 0.05),
      stepTime: 0.5 + (j * 0.001) + (Math.random() * 0.05),
      strideVariability: 0.02 + (j * 0.0005) + (Math.random() * 0.01),
      stanceTime: 0.4 + (j * 0.001) + (Math.random() * 0.05),
      turningSpeed: 90 - (j * 0.2) + (Math.random() * 5),
      wanderingEvents: Math.floor(Math.random() * 5)
    };
  });

  return {
    id: `RES_${i + 1}`,
    name: `Resident ${i + 1}`,
    age: 65 + Math.floor(Math.random() * 20),
    diagnosis: ['Early AD', 'Mild AD', 'Moderate AD', 'MCI', 'Mixed Dementia'][Math.floor(Math.random() * 5)],
    diagnosisDate,
    riskLevel: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high',
    gaitHistory,
    cohortGroup: ['Early Stage', 'Mid Stage', 'Late Stage'][Math.floor(Math.random() * 3)]
  };
});

const generateInterventionData = (baseRisk: number, months: number): InterventionData[] => {
  return Array.from({ length: months }, (_, i) => {
    const monthData = {
      month: i + 1,
      riskScore: Math.max(0.1, Math.min(0.9, baseRisk + (Math.random() * 0.1 - 0.05) - (i * 0.01))),
      interventionCount: Math.floor(Math.random() * 5) + 3,
      adherenceRate: 0.7 + (Math.random() * 0.2)
    };
    return monthData;
  });
};

const mockAggregateData: AggregateMetrics[] = [
  {
    diagnosisGroup: 'Early AD',
    averageProgression: 0.15,
    patientCount: 45,
    metrics: {
      avgWalkingSpeed: 1.1,
      avgStrideVariability: 0.03,
      avgTurningSpeed: 85,
      avgWanderingEvents: 1.2,
      riskScore: 0.35
    },
    timeToDeterioration: 24,
    interventionHistory: generateInterventionData(0.35, 12)
  },
  {
    diagnosisGroup: 'Mild AD',
    averageProgression: 0.25,
    patientCount: 38,
    metrics: {
      avgWalkingSpeed: 0.9,
      avgStrideVariability: 0.05,
      avgTurningSpeed: 75,
      avgWanderingEvents: 2.1,
      riskScore: 0.55
    },
    timeToDeterioration: 18,
    interventionHistory: generateInterventionData(0.55, 12)
  },
  {
    diagnosisGroup: 'Moderate AD',
    averageProgression: 0.4,
    patientCount: 32,
    metrics: {
      avgWalkingSpeed: 0.7,
      avgStrideVariability: 0.08,
      avgTurningSpeed: 65,
      avgWanderingEvents: 3.5,
      riskScore: 0.75
    },
    timeToDeterioration: 12,
    interventionHistory: generateInterventionData(0.75, 12)
  },
  {
    diagnosisGroup: 'MCI',
    averageProgression: 0.1,
    patientCount: 56,
    metrics: {
      avgWalkingSpeed: 1.2,
      avgStrideVariability: 0.02,
      avgTurningSpeed: 90,
      avgWanderingEvents: 0.8,
      riskScore: 0.25
    },
    timeToDeterioration: 36,
    interventionHistory: generateInterventionData(0.25, 12)
  }
];

const predictiveFactors = [
  { factor: 'Stride Variability', correlation: 0.85, significance: 'High', timeToDetection: '3-6 months' },
  { factor: 'Walking Speed', correlation: 0.78, significance: 'High', timeToDetection: '4-8 months' },
  { factor: 'Turning Speed', correlation: 0.72, significance: 'Medium', timeToDetection: '5-9 months' },
  { factor: 'Wandering Events', correlation: 0.68, significance: 'Medium', timeToDetection: '2-5 months' },
  { factor: 'Step Length', correlation: 0.65, significance: 'Medium', timeToDetection: '6-12 months' }
];

const NeuroGaitAnalytics: React.FC = () => {
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<keyof GaitMetrics>('walkingSpeed');
  const [timeRange, setTimeRange] = useState<number>(30); // days
  const [cohortView, setCohortView] = useState<boolean>(false);
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);

  const cohortData = useMemo(() => {
    if (!selectedResident) return [];
    
    return mockResidents
      .filter(r => r.cohortGroup === selectedResident.cohortGroup)
      .map(r => ({
        id: r.id,
        name: r.name,
        walkingSpeed: r.gaitHistory[0].walkingSpeed,
        strideVariability: r.gaitHistory[0].strideVariability,
        highlighted: r.id === selectedResident.id
      }));
  }, [selectedResident]);

  const metrics = [
    { key: 'walkingSpeed', label: 'Walking Speed (m/s)', threshold: 0.8 },
    { key: 'stepLength', label: 'Step Length (m)', threshold: 0.5 },
    { key: 'strideVariability', label: 'Stride Variability', threshold: 0.05 },
    { key: 'turningSpeed', label: 'Turning Speed (deg/s)', threshold: 80 },
    { key: 'wanderingEvents', label: 'Wandering Events', threshold: 3 }
  ];

  const exportData = () => {
    if (!selectedResident) return;
    const csvContent = [
      ['Date', 'Walking Speed', 'Step Length', 'Stride Variability', 'Turning Speed', 'Wandering Events'].join(','),
      ...selectedResident.gaitHistory.map(record => 
        [record.timestamp.toISOString(), record.walkingSpeed, record.stepLength, 
         record.strideVariability, record.turningSpeed, record.wanderingEvents].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedResident.name}_gait_analysis.csv`;
    a.click();
  };

  const renderAdvancedAnalytics = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold">Advanced Analytics & Predictions</h3>
            <p className="text-gray-600">Aggregate analysis of gait patterns and disease progression</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => setShowAdvancedAnalytics(false)}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
            >
              Return to Individual View
            </button>
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Download size={18} />
              Export Analysis
            </button>
          </div>
        </div>

        {/* Predictive Factors */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold mb-4">Key Predictive Factors</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={predictiveFactors}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="factor" />
                  <YAxis domain={[0, 1]} />
                  <Tooltip />
                  <Bar dataKey="correlation" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {predictiveFactors.map(factor => (
                <div key={factor.factor} className="bg-purple-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-semibold">{factor.factor}</h5>
                      <p className="text-sm text-gray-600">Correlation: {factor.correlation.toFixed(2)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      factor.significance === 'High' ? 'bg-purple-100 text-purple-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {factor.significance} Impact
                    </span>
                  </div>
                  <p className="text-sm mt-2">
                    Early detection window: {factor.timeToDetection}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Progression Analysis */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold mb-4">Disease Progression Analysis</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={mockAggregateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="diagnosisGroup" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="patientCount" fill="#8b5cf6" name="Patient Count" />
                  <Line yAxisId="right" type="monotone" dataKey="averageProgression" stroke="#2563eb" name="Progression Rate" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {mockAggregateData.map(group => (
                <div key={group.diagnosisGroup} className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-semibold">{group.diagnosisGroup}</h5>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Avg. Walking Speed</p>
                      <p className="font-medium">{group.metrics.avgWalkingSpeed.toFixed(2)} m/s</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Stride Variability</p>
                      <p className="font-medium">{(group.metrics.avgStrideVariability * 100).toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Risk Score</p>
                      <p className="font-medium">{(group.metrics.riskScore * 100).toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Time to Deterioration</p>
                      <p className="font-medium">{group.timeToDeterioration} months</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Intervention Effectiveness */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h4 className="text-lg font-semibold mb-4">Intervention Effectiveness</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {mockAggregateData.map(group => (
              <div key={group.diagnosisGroup} className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h5 className="font-semibold text-lg">{group.diagnosisGroup}</h5>
                    <p className="text-sm text-gray-600">
                      {group.patientCount} patients monitored
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-purple-600">
                      {(group.metrics.riskScore * 100).toFixed(1)}% current risk
                    </span>
                  </div>
                </div>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={group.interventionHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" label={{ value: 'Months', position: 'bottom' }} />
                      <YAxis yAxisId="left" domain={[0, 1]} label={{ value: 'Risk Score', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 1]} label={{ value: 'Adherence', angle: 90, position: 'insideRight' }} />
                      <Tooltip 
                        formatter={(value: number, name: string) => [
                          `${(value * 100).toFixed(1)}%`,
                          name === 'riskScore' ? 'Risk Score' : 'Adherence Rate'
                        ]}
                      />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="riskScore" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        name="Risk Score"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="adherenceRate" 
                        stroke="#2563eb" 
                        strokeWidth={2}
                        name="Adherence Rate"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Avg. Monthly Interventions</p>
                    <p className="font-medium text-lg">
                      {(group.interventionHistory.reduce((acc, curr) => acc + curr.interventionCount, 0) / 
                        group.interventionHistory.length).toFixed(1)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg. Adherence Rate</p>
                    <p className="font-medium text-lg">
                      {(group.interventionHistory.reduce((acc, curr) => acc + curr.adherenceRate, 0) / 
                        group.interventionHistory.length * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-800">
                    Risk Reduction: {(
                      (group.interventionHistory[0].riskScore - 
                       group.interventionHistory[group.interventionHistory.length - 1].riskScore) * 100
                    ).toFixed(1)}%
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    Over {group.interventionHistory.length} months of monitoring
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="text-purple-600" />
            {showAdvancedAnalytics ? 'Advanced Gait Analytics' : 'Memory Care Monitoring'}
          </h2>
          <p className="text-gray-600 mt-1">
            {showAdvancedAnalytics 
              ? 'Analyze patterns and predict disease progression'
              : 'Track resident gait patterns and early indicators of cognitive decline'}
          </p>
        </div>
        <div className="flex gap-4">
          {selectedResident && !showAdvancedAnalytics && (
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
            >
              <Download size={18} />
              Export Data
            </button>
          )}
          {!showAdvancedAnalytics && (
            <button
              onClick={() => setShowAdvancedAnalytics(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <BarChart2 size={18} />
              Advanced Analytics
            </button>
          )}
        </div>
      </div>

      {showAdvancedAnalytics ? renderAdvancedAnalytics() : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockResidents.map(resident => (
              <div
                key={resident.id}
                className={`bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow
                  ${selectedResident?.id === resident.id ? 'ring-2 ring-purple-500' : ''}`}
                onClick={() => setSelectedResident(resident)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{resident.name}</h3>
                    <p className="text-sm text-gray-600">{resident.diagnosis}</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm
                    ${resident.riskLevel === 'high' ? 'bg-red-100 text-red-800' :
                      resident.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'}`}
                  >
                    {resident.riskLevel.charAt(0).toUpperCase() + resident.riskLevel.slice(1)} Risk
                  </div>
                </div>

                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={resident.gaitHistory.slice(0, 30)}>
                      <Line
                        type="monotone"
                        dataKey="walkingSpeed"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">Walking Speed</div>
                    <div className="font-semibold">
                      {resident.gaitHistory[0].walkingSpeed.toFixed(2)} m/s
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600">Stride Variability</div>
                    <div className="font-semibold">
                      {(resident.gaitHistory[0].strideVariability * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedResident && (
            <>
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-semibold">{selectedResident.name} - Detailed Analysis</h3>
                    <p className="text-gray-600">
                      Diagnosed with {selectedResident.diagnosis} on{' '}
                      {selectedResident.diagnosisDate.toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <select
                      className="border rounded-lg px-3 py-2"
                      value={timeRange}
                      onChange={(e) => setTimeRange(Number(e.target.value))}
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                    <button
                      className={`px-4 py-2 rounded-lg ${cohortView ? 
                        'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                      onClick={() => setCohortView(!cohortView)}
                    >
                      <Users size={18} className="inline mr-2" />
                      Cohort View
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-4 mb-6">
                  {metrics.map(metric => (
                    <button
                      key={metric.key}
                      className={`p-4 rounded-lg text-left ${
                        selectedMetric === metric.key ? 
                        'bg-purple-50 border-2 border-purple-500' : 
                        'bg-gray-50 border-2 border-transparent'
                      }`}
                      onClick={() => setSelectedMetric(metric.key as keyof GaitMetrics)}
                    >
                      <div className="text-sm text-gray-600">{metric.label}</div>
                      <div className="font-semibold mt-1">
                        {(selectedResident.gaitHistory[0][metric.key as keyof GaitMetrics] as number).toFixed(2)}
                      </div>
                      <div className={`text-xs mt-1 ${
                        (selectedResident.gaitHistory[0][metric.key as keyof GaitMetrics] as number) < metric.threshold ?
                        'text-red-600' : 'text-green-600'
                      }`}>
                        Threshold: {metric.threshold}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="h-96">
                  {!cohortView ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={selectedResident.gaitHistory.slice(0, timeRange)}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={(date) => new Date(date).toLocaleDateString()}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(label) => new Date(label).toLocaleDateString()}
                          formatter={(value: number) => [value.toFixed(3), metrics.find(m => m.key === selectedMetric)?.label]}
                        />
                        <Line
                          type="monotone"
                          dataKey={selectedMetric}
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart
                        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                      >
                        <CartesianGrid />
                        <XAxis 
                          type="number" 
                          dataKey="walkingSpeed" 
                          name="Walking Speed" 
                          unit="m/s"
                        />
                        <YAxis 
                          type="number" 
                          dataKey="strideVariability" 
                          name="Stride Variability" 
                          unit="%" 
                        />
                        <ZAxis 
                          type="category" 
                          dataKey="name" 
                        />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Legend />
                        <Scatter 
                          name="Cohort Analysis" 
                          data={cohortData} 
                          fill="#8884d8"
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <SpatialNavigationAnalytics 
                  residentId={selectedResident.id}
                  residentName={selectedResident.name}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NeuroGaitAnalytics; 
